import {
  User,
  UserManager,
  WebStorageStateStore,
  type IdTokenClaims,
  type UserManagerSettings,
} from 'oidc-client-ts'
import type { components } from './openapi.generated'
import type { Replace } from './type-utils'

type Schemas = components['schemas']

const oidcScope = 'openid profile email urn:zitadel:iam:user:resourceowner'
const portalOidcScope = `${oidcScope} offline_access`

export type AuthConfig = Replace<Schemas['AuthConfigDto'], 'mailpitUrl', {
  mailpitUrl: string | null
}>

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function requireString(value: unknown, field: string): string {
  if (typeof value !== 'string') throw new Error(`Expected a string for "${field}"`)
  return value
}

function requireNumber(value: unknown, field: string): number {
  if (typeof value !== 'number') throw new Error(`Expected a number for "${field}"`)
  return value
}

function decodeAuthConfig(raw: unknown): AuthConfig {
  if (!isRecord(raw)) throw new Error('Malformed auth config')
  return {
    issuer: requireString(raw.issuer, 'issuer'),
    clientId: requireString(raw.clientId, 'clientId'),
    projectId: requireString(raw.projectId, 'projectId'),
    redirectUri: requireString(raw.redirectUri, 'redirectUri'),
    mailpitUrl:
      raw.mailpitUrl === null || raw.mailpitUrl === undefined
        ? null
        : requireString(raw.mailpitUrl, 'mailpitUrl'),
  } satisfies AuthConfig
}

export type OidcBootstrap =
  | { available: false; userManager: null }
  | { available: true; userManager: UserManager }

let bootstrap: OidcBootstrap | null = null
let inFlight: Promise<OidcBootstrap> | null = null
let mailpitUrl: string | null = null
let federatedIdentityRedirectUri: string | null = null
let userManagerSettings: UserManagerSettings | null = null
let audienceScope = ''
let ssoTestManager: UserManager | null = null

export async function initOidc(): Promise<OidcBootstrap> {
  if (bootstrap) return bootstrap
  if (inFlight) return inFlight

  inFlight = (async (): Promise<OidcBootstrap> => {
    let resp: Response
    try {
      resp = await fetch('/api/v1/auth/config', { headers: { Accept: 'application/json' } })
    } catch (e) {
      console.warn('[oidc] /api/v1/auth/config fetch failed; OIDC unavailable', e)
      bootstrap = { available: false, userManager: null }
      return bootstrap
    }

    if (!resp.ok) {
      console.warn(`[oidc] /api/v1/auth/config returned ${resp.status}; OIDC unavailable`)
      bootstrap = { available: false, userManager: null }
      return bootstrap
    }

    let config: AuthConfig
    try {
      config = decodeAuthConfig(await resp.json())
    } catch (e) {
      console.warn('[oidc] /api/v1/auth/config returned malformed JSON; OIDC unavailable', e)
      bootstrap = { available: false, userManager: null }
      return bootstrap
    }

    mailpitUrl = config.mailpitUrl ?? null
    federatedIdentityRedirectUri = new URL('/idps/callback', config.issuer).toString()
    // The Host accepts tokens by the platform project audience, so a domain's own project asks for it too.
    audienceScope = `urn:zitadel:iam:org:project:id:${config.projectId}:aud`

    userManagerSettings = {
      authority: config.issuer,
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      silent_redirect_uri: config.redirectUri,
      post_logout_redirect_uri: window.location.origin,
      response_type: 'code',
      // The Host resolves federated users through their Zitadel resource-owner org.
      // Without this reserved scope, a valid Entra login looks like default-org JIT.
      scope: [portalOidcScope, audienceScope].join(' '),
      automaticSilentRenew: true,
      silentRequestTimeoutInSeconds: 10,
      revokeTokensOnSignout: true,
      loadUserInfo: false,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
    }
    const userManager = new UserManager(userManagerSettings)

    bootstrap = { available: true, userManager }
    return bootstrap
  })()

  try {
    return await inFlight
  } finally {
    inFlight = null
  }
}

export function getOidcBootstrap(): OidcBootstrap | null {
  return bootstrap
}

export function getMailpitUrl(): string | null {
  return mailpitUrl
}

export function getFederatedIdentityRedirectUri(): string | null {
  return federatedIdentityRedirectUri
}

export function getUserManager(): UserManager | null {
  return bootstrap?.available ? bootstrap.userManager : null
}

export function getOidcRedirectUri(): string | null {
  return typeof userManagerSettings?.redirect_uri === 'string'
    ? userManagerSettings.redirect_uri
    : null
}

export async function signinRedirect(
  loginHint?: string,
  state?: Record<string, unknown>,
): Promise<void> {
  const um = getUserManager()
  if (!um) throw new Error('OIDC not initialized or in dev mode')
  const args: Parameters<UserManager['signinRedirect']>[0] = {}
  if (loginHint) args.extraQueryParams = { login_hint: loginHint }
  if (state) args.state = state
  await um.signinRedirect(Object.keys(args).length > 0 ? args : undefined)
}

export function isLocalRedirect(value: unknown): value is string {
  return typeof value === 'string' &&
    value.startsWith('/') &&
    !value.startsWith('//') &&
    !value.includes('\\')
}

export type SsoTestResult = {
  email: string | null
  name: string
  subject: string
  issuer: string
  organizationId: string
  providerId: string
  applicationRoles: string[]
  groups: string[]
  groupsOverage: boolean
}

function stringList(value: unknown): string[] {
  if (typeof value === 'string') return [value]
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string')
}

function decodeAccessTokenClaims(token: string): Record<string, unknown> {
  try {
    const decoded = decodeJwtPayload(token)
    return isRecord(decoded) ? decoded : {}
  } catch {
    return {}
  }
}

function decodeFederatedEntitlements(claims: Record<string, unknown>): {
  providerId?: string
  roles: string[]
  groups: string[]
  groupsOverage: boolean
} {
  const metadata = claims['urn:zitadel:iam:user:metadata']
  if (!isRecord(metadata)) return { roles: [], groups: [], groupsOverage: false }
  const encoded = metadata['vantage.federated.entitlements']
  if (typeof encoded !== 'string') return { roles: [], groups: [], groupsOverage: false }
  try {
    const bytes = Uint8Array.from(atob(encoded), (character) => character.charCodeAt(0))
    const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes))
    if (!isRecord(parsed)) return { roles: [], groups: [], groupsOverage: false }
    return {
      providerId: typeof parsed.providerId === 'string' ? parsed.providerId : undefined,
      roles: stringList(parsed.roles),
      groups: stringList(parsed.groups),
      groupsOverage: parsed.groupsOverage === true,
    }
  } catch {
    return { roles: [], groups: [], groupsOverage: false }
  }
}

/** Tests a saved entity provider without replacing the administrator's current session. */
export async function testSso(
  entityId: string,
  zitadelOrgId: string,
  idpId: string,
): Promise<SsoTestResult> {
  if (!userManagerSettings) throw new Error('OIDC not initialized or in dev mode')

  ssoTestManager ??= new UserManager({
    ...userManagerSettings,
    automaticSilentRenew: false,
    popup_redirect_uri: `${window.location.origin}/auth/callback`,
    userStore: new WebStorageStateStore({
      store: window.sessionStorage,
      prefix: 'oidc.sso-test.',
    }),
  })

  const user = await ssoTestManager.signinPopup({
    prompt: 'login',
    scope: [
      oidcScope,
      audienceScope,
      'urn:zitadel:iam:user:metadata',
      `urn:zitadel:iam:org:id:${zitadelOrgId}`,
      `urn:zitadel:iam:org:idp:id:${idpId}`,
    ].join(' '),
  })
  const response = await fetch(`/api/v1/me?entity_id=${encodeURIComponent(entityId)}`, {
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${user.access_token}`,
    },
  })
  if (!response.ok) {
    throw new Error('Authentication succeeded, but the platform could not validate the test user.')
  }
  const result: unknown = await response.json()
  if (!isRecord(result) || (typeof result.email !== 'string' && result.email !== null)) {
    throw new Error('Authentication succeeded, but the platform returned an invalid user profile.')
  }
  const claims = {
    ...decodeAccessTokenClaims(user.access_token),
    ...user.profile,
  }
  const entitlements = decodeFederatedEntitlements(claims)
  const profileName = typeof result.name === 'string' ? result.name : null
  const claimName = typeof claims.name === 'string' ? claims.name : null
  return {
    email: result.email,
    name: profileName ?? claimName ?? result.email ?? 'Unnamed user',
    subject: typeof claims.sub === 'string' ? claims.sub : '',
    issuer: typeof claims.iss === 'string' ? claims.iss : '',
    organizationId: typeof claims['urn:zitadel:iam:user:resourceowner:id'] === 'string'
      ? claims['urn:zitadel:iam:user:resourceowner:id']
      : zitadelOrgId,
    providerId: entitlements.providerId ?? idpId,
    applicationRoles: [...new Set([
      ...entitlements.roles,
      ...stringList(claims.roles),
    ])],
    groups: [...new Set([
      ...entitlements.groups,
      ...stringList(claims.groups),
    ])],
    groupsOverage: entitlements.groupsOverage
      || claims.hasgroups === true
      || isRecord(claims._claim_names) && 'groups' in claims._claim_names,
  }
}

export async function signoutRedirect(): Promise<void> {
  const um = getUserManager()
  if (!um) throw new Error('OIDC not initialized or in dev mode')
  await um.signoutRedirect()
}

// Mints a session for a seeded persona (the Host holds the password) and
// injects it into the existing user store so the normal session machinery —
// `Bearer` attach, sign-out, reload — works unchanged.
export async function devSignin(email: string): Promise<string> {
  const resp = await fetch('/api/v1/dev/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email }),
  })
  if (!resp.ok) {
    throw new Error(`Dev sign-in failed (${resp.status})`)
  }
  const raw: unknown = await resp.json()
  if (!isRecord(raw)) throw new Error('Malformed dev sign-in response')
  const accessToken = requireString(raw.accessToken, 'accessToken')
  const expiresInSeconds = requireNumber(raw.expiresInSeconds, 'expiresInSeconds')

  await storeMintedSession(accessToken, expiresInSeconds)
  return accessToken
}

export async function storeMintedSession(
  accessToken: string,
  expiresInSeconds: number,
): Promise<void> {
  const um = getUserManager()
  if (!um) return
  const profile = decodeJwtPayload(accessToken)
  await um.storeUser(
    new User({
      access_token: accessToken,
      token_type: 'Bearer',
      profile,
      expires_at: Math.floor(Date.now() / 1000) + expiresInSeconds,
      scope: oidcScope,
    }),
  )
}

function decodeJwtPayload(token: string): IdTokenClaims {
  const segment = token.split('.')[1] ?? ''
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
  return JSON.parse(atob(padded)) as IdTokenClaims
}

export function __resetOidcForTests(): void {
  bootstrap = null
  inFlight = null
  mailpitUrl = null
  federatedIdentityRedirectUri = null
  userManagerSettings = null
  ssoTestManager = null
}
